'use client'

import { cn } from '@/lib/utils'
import { useMedia } from 'react-use'

type TPreviewIframeProps = {
  previewUrl: string
  templateTitle: string
  deviceView: 'desktop' | 'mobile'
}

/**
 * Template preview iframe component with responsive scaling
 * @param previewUrl - URL to preview in iframe
 * @param templateTitle - Title of the template for accessibility
 * @param deviceView - Current device view (desktop or mobile)
 * @returns Preview iframe component
 */
export function PreviewIframe({
  previewUrl,
  templateTitle,
  deviceView,
}: TPreviewIframeProps) {
  const isWide = useMedia('(min-width: 480px)', true)
  const isLg = useMedia('(min-width: 1024px)', true)
  const isXl = useMedia('(min-width: 1280px)', true)

  /**
   * Calculate preview scale based on device view and viewport
   * @returns Scale value for iframe transformation
   */
  const getPreviewScale = () => {
    if (deviceView === 'mobile') {
      if (isXl) return 0.6
      if (isLg) return 0.6
      if (isWide) return 0.6
      return 0.8
    } else {
      if (isXl) return 0.7
      if (isLg) return 0.7
      if (isWide) return 0.7
      return 0.7
    }
  }

  const previewScale = getPreviewScale()
  const containerScale = 100 / previewScale

  /**
   * Calculate minimum height for xl breakpoint
   * @returns Minimum height in pixels
   */
  const getMinHeight = () => {
    const xlContainerWidth = 960
    const xlHeight = xlContainerWidth * (9 / 16)
    return xlHeight
  }

  const minHeight = getMinHeight()

  return (
    <div
      className={cn(
        'w-full aspect-video bg-white dark:bg-dark rounded-lg shadow-xl overflow-hidden transition-all duration-300 border border-gray-700 relative',
        deviceView === 'mobile' ? 'max-w-[300px] mx-auto' : 'w-full',
      )}
      style={{
        minHeight: `${minHeight}px`,
      }}
    >
      <iframe
        src={previewUrl}
        allow="microphone; cross-origin-isolated"
        className="absolute inset-0 rounded-lg"
        style={{
          width: `${containerScale}%`,
          height: `${containerScale}%`,
          transform: `scale(${previewScale})`,
          transformOrigin: 'top left',
          scrollbarWidth: 'thin' as const,
          scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent',
        }}
        title={`Preview of ${templateTitle}`}
      />
    </div>
  )
}
